import React, { useState, useEffect } from 'react';
import { BatchRecord } from '../../../types/batchRecord';

interface SignOffRow {
  label: string;
  nameField: keyof BatchRecord;
  dateField: keyof BatchRecord;
}

interface SignOffSectionProps {
  record: BatchRecord;
  onUpdate: (record: BatchRecord) => void;
}

export const SignOffSection: React.FC<SignOffSectionProps> = ({
  record,
  onUpdate
}) => {
  const [editedData, setEditedData] = useState<BatchRecord>(record);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    setEditedData(record);
    setHasChanges(false);
  }, [record]);

  const signOffRows: SignOffRow[] = [
    {
      label: 'Production (Staff)',
      nameField: 'productionStaffName',
      dateField: 'productionStaffDate'
    },
    {
      label: 'Supervisor (Quality Assurance)',
      nameField: 'supervisorQaName',
      dateField: 'supervisorQaDate'
    }
  ];

  const handleFieldChange = (field: keyof BatchRecord, value: any) => {
    setEditedData({
      ...editedData,
      [field]: value
    });
    setHasChanges(true);
  };

  const handleSave = () => {
    onUpdate(editedData);
    setHasChanges(false);
  };

  const handleCancel = () => {
    setEditedData(record);
    setHasChanges(false);
  };

  const inputStyle: React.CSSProperties = {
    padding: '8px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '14px',
    width: '100%',
    boxSizing: 'border-box'
  };

  const buttonStyle: React.CSSProperties = {
    padding: '8px 16px',
    borderRadius: '4px',
    border: 'none',
    fontWeight: '500',
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'background-color 0.2s'
  };

  return (
    <div>
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: '14px',
          backgroundColor: 'white'
        }}
      >
        <thead>
          <tr>
            <th
              style={{
                padding: '12px 8px',
                backgroundColor: '#f8f9fa',
                fontWeight: '600',
                textAlign: 'left',
                borderBottom: '2px solid #e0e0e0',
                textTransform: 'uppercase',
                fontSize: '12px',
                letterSpacing: '0.5px',
                color: '#555',
                width: '30%'
              }}
            >
              Role
            </th>
            <th
              style={{
                padding: '12px 8px',
                backgroundColor: '#f8f9fa',
                fontWeight: '600',
                textAlign: 'left',
                borderBottom: '2px solid #e0e0e0',
                textTransform: 'uppercase',
                fontSize: '12px',
                letterSpacing: '0.5px',
                color: '#555',
                width: '40%'
              }}
            >
              Name
            </th>
            <th
              style={{
                padding: '12px 8px',
                backgroundColor: '#f8f9fa',
                fontWeight: '600',
                textAlign: 'left',
                borderBottom: '2px solid #e0e0e0',
                textTransform: 'uppercase',
                fontSize: '12px',
                letterSpacing: '0.5px',
                color: '#555',
                width: '30%'
              }}
            >
              Date
            </th>
          </tr>
        </thead>
        <tbody>
          {signOffRows.map((row, index) => {
            const name = editedData[row.nameField] as string | undefined;
            const date = editedData[row.dateField] as string | undefined;

            return (
              <tr key={row.label}>
                <td
                  style={{
                    padding: '12px 8px',
                    borderBottom: index < signOffRows.length - 1 ? '1px solid #eee' : 'none',
                    fontWeight: '500',
                    color: '#333'
                  }}
                >
                  {row.label}
                </td>
                <td
                  id={`batch-${record.id}-field-${String(row.nameField)}`}
                  style={{
                    padding: '12px 8px',
                    borderBottom: index < signOffRows.length - 1 ? '1px solid #eee' : 'none'
                  }}
                >
                  <input
                    type="text"
                    value={name ?? ''}
                    onChange={e =>
                      handleFieldChange(
                        row.nameField,
                        e.target.value || undefined
                      )
                    }
                    style={inputStyle}
                    placeholder="Enter name"
                  />
                </td>
                <td
                  id={`batch-${record.id}-field-${String(row.dateField)}`}
                  style={{
                    padding: '12px 8px',
                    borderBottom: index < signOffRows.length - 1 ? '1px solid #eee' : 'none'
                  }}
                >
                  <input
                    type="date"
                    value={date ? date.split('T')[0] : ''}
                    onChange={e =>
                      handleFieldChange(
                        row.dateField,
                        e.target.value || undefined
                      )
                    }
                    style={inputStyle}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {hasChanges && (
        <div style={{ marginTop: '16px', display: 'flex', gap: '8px' }}>
          <button
            onClick={handleSave}
            style={{
              ...buttonStyle,
              backgroundColor: '#4CAF50',
              color: 'white'
            }}
            onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#45a049')}
            onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#4CAF50')}
          >
            Save Sign-Off
          </button>
          <button
            onClick={handleCancel}
            style={{
              ...buttonStyle,
              backgroundColor: '#757575',
              color: 'white'
            }}
            onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#616161')}
            onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#757575')}
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
};
